import { Card, Text } from "@marekl/adminer/components";
import { MusicPlayer } from "../../../components/MusicPlayer";
import { formatDuration, formatFileSize } from "../../../lib/musicState";
import { recordingFileUrl } from "../../../lib/queries/music";
import type { Discipline, Recording } from "../../../lib/queries/rules";

/**
 * Which official ČNSMS track this discipline is danced to by default.
 *
 * Read-only, and deliberately so on both counts. The recordings belong
 * to the rule set (the „Hudba" tab owns adding and replacing them), and
 * the discipline's choice among them is one of its defaults, so it is
 * edited where the others are — „Upravit disciplínu". This card exists
 * to say which track applies and to let it be heard.
 *
 * The wording matters: the choice is a DEFAULT, not a property of the
 * whole discipline. TD Twirl's tým combinations override it with a
 * longer recording, which is the case the old per-discipline upload
 * could not express at all.
 */
export function DisciplineRecordingCard({
  discipline,
  recordings,
}: {
  discipline: Discipline;
  recordings: Recording[];
}) {
  const current = recordings.find((r) => r.id === discipline.default_recording_id);

  return (
    <Card>
      <Card.Header
        title="Oficiální hudba ČNSMS"
        subtitle={current ? current.name : "nenastaveno"}
        subtitlePlacement="right"
      />
      <Card.Content>
        <Text variant="muted">
          Platí pro kombinace, které nemají vybranou jinou nahrávku ČNSMS. Nahrávky spravujete
          v záložce „Hudba“ celé sady pravidel, výchozí volbu změníte v „Upravit
          disciplínu“; týmy si ji mohou přehrát i stáhnout na trénink a do exportu pro
          zvukaře se vloží ke každému startovnímu číslu.
        </Text>

        {current ? (
          <>
            <Text variant="muted">
              {formatDuration(current.duration_ms)} · {current.bitrate_kbps} kbps ·{" "}
              {formatFileSize(current.size_bytes)}
            </Text>
            {/* No time-limit line: the combinations resolving to this
                track may allow different lengths, so there is no single
                limit to draw. */}
            <MusicPlayer src={recordingFileUrl(current.id)} />
          </>
        ) : (
          <Text variant="muted">
            Disciplína se soutěží na hudbu ČNSMS, ale žádná nahrávka jí zatím není
            přiřazená.
          </Text>
        )}
      </Card.Content>
    </Card>
  );
}
